import { Box, Button, Stack, TableCell, TableRow, styled, useTheme } from '@mui/material';
import { DeleteIcon } from '@synergy/theme-next';
import { FormattedMessage, FormattedNumber } from 'react-intl';
import { PoolBanner, PoolImage, PoolLink, PoolQuantityPicker, PoolRow, PoolTextButton } from '@synergy/components-next';
import { PoolFormattedMessage } from '@synergy/components-next';
import { PoolMath } from '@synergy/utils';
import { ReactElement, useState } from 'react';

import { ArticlePrice } from './article-price';
import { Attribute } from '../../../../models/attribute';
import { DeliveryStatus } from '../../../../components/delivery-status/delivery-status';
import { FavoriteHeartIcon } from '../../../../components/favorite-button/components/favorite-heart-icon';
import { ROUTES } from '../../../../utils/routes';
import { ShoppingCartItem, ShoppingCartItemStatus } from '../../../../models/shopping-cart';
import { VerticalDivider } from '../../../../components/vertical-divider/vertical-divider';
import { addToWishlist } from '../../../../store/whish-list-actions';
import { getBannerMessage } from '../utils/get-banner-message';
import { openInquiryModal } from '../../../../store/single-inquiry-actions';
import { removeArticleFromCart, setArticleQuantity } from '../../../../store/shopping-cart-article-actions';
import { useAppDispatch } from '../../../../../store/store';

const ProductImage = styled(PoolImage)(() => ({
    display: 'block',
    objectFit: 'contain',
    height: 110,
    width: 110,
}));

export interface SupplierTableRowProps {
    cartItem: ShoppingCartItem;
    previewImage: string | undefined;
}

export const RowArticle = (props: SupplierTableRowProps): ReactElement => {
    const theme = useTheme();
    const dispatch = useAppDispatch();
    const { cartItem, previewImage } = props;
    const article = cartItem.product;

    const [isFavorite, setIsFavorite] = useState(article.isFavorite);
    const [quantity, setQuantity] = useState(cartItem.quantity);

    const handleAddToWishlist = (): void => {
        if (!isFavorite) {
            setIsFavorite(true);
            dispatch(addToWishlist('default', article.id, cartItem.quantity));
        }
    };

    const handleCreateSingleInquiry = (): void => {
        dispatch(
            openInquiryModal({
                items: [{ id: article.id, articleName: article.name, quantity: article.minimumOrderQuantity }],
            })
        );
    };

    const handleDeleteArticle = (): void => {
        dispatch(removeArticleFromCart(cartItem.id));
    };

    const handleQuantityChange = (quantity: number): void => {
        dispatch(setArticleQuantity(cartItem.id, quantity));
        setQuantity(quantity);
    };

    return (
        <>
            {cartItem.status !== ShoppingCartItemStatus.Available && (
                <TableRow>
                    <TableCell colSpan={4} sx={{ px: 0, pb: 0, pt: 2, borderBottom: 'none' }}>
                        <PoolBanner severity={cartItem.status === ShoppingCartItemStatus.Changed ? 'warning' : 'error'}>
                            <FormattedMessage id={getBannerMessage(cartItem.status)} />
                        </PoolBanner>
                    </TableCell>
                </TableRow>
            )}

            <TableRow>
                <TableCell>
                    <PoolRow alignItems="start" color="grey.800" gap={3}>
                        <Stack justifyContent="center">
                            <PoolLink href={ROUTES.article.detail.replace(':id', article.id.toString())}>
                                <ProductImage
                                    src={previewImage}
                                    alt={article.name}
                                    sx={{
                                        opacity: cartItem.status !== ShoppingCartItemStatus.Removed ? 1 : 0.5,
                                    }}
                                />
                            </PoolLink>
                        </Stack>

                        <Stack gap={1.5} typography="body2">
                            <Stack
                                gap={0.5}
                                sx={{ opacity: cartItem.status !== ShoppingCartItemStatus.Removed ? 1 : 0.5 }}
                            >
                                <PoolLink
                                    colorVariant="black"
                                    href={ROUTES.article.detail.replace(':id', article.id.toString())}
                                    typography="subtitle1"
                                >
                                    {article?.name}
                                </PoolLink>
                                <PoolRow divider={<VerticalDivider />} flexWrap="wrap">
                                    <PoolFormattedMessage
                                        id={'app.cart.article.articleNumber'}
                                        values={{ articleNumber: article.number }}
                                    />
                                    {article.globalTradeItemNumber && (
                                        <PoolFormattedMessage
                                            id={'app.cart.article.globalNumber'}
                                            values={{ articleNumber: article.globalTradeItemNumber }}
                                        />
                                    )}
                                </PoolRow>
                                {article.attributes.length !== 0 && (
                                    <PoolRow divider={<VerticalDivider />} flexWrap="wrap">
                                        {article?.attributes.map((attribute: Attribute) => {
                                            return (
                                                <Box key={attribute.name}>
                                                    {attribute.name} {attribute.value}
                                                </Box>
                                            );
                                        })}
                                    </PoolRow>
                                )}
                            </Stack>

                            <PoolRow sx={{ my: 1.25 }} flexWrap="wrap" gap={3} rowGap={1}>
                                <DeliveryStatus
                                    variant="circle"
                                    deliveryStatus={article.deliveryStatus}
                                    stockStatus={article.stockStatus}
                                    deliveryTimeInDays={article.deliveryTimeInDays}
                                />
                                {cartItem.status !== ShoppingCartItemStatus.Removed && (
                                    <PoolTextButton
                                        startIcon={<FavoriteHeartIcon isFavorite={isFavorite} color="inherit" />}
                                        onClick={handleAddToWishlist}
                                    >
                                        <PoolFormattedMessage id={'app.cart.article.remember'} />
                                    </PoolTextButton>
                                )}

                                <PoolTextButton
                                    startIcon={<DeleteIcon />}
                                    onClick={handleDeleteArticle}
                                    color={cartItem.status === ShoppingCartItemStatus.Changed ? 'gray.700' : 'error'}
                                    hoverColor={cartItem.status === ShoppingCartItemStatus.Changed ? 'error' : 'black'}
                                >
                                    <PoolFormattedMessage id={'app.cart.article.delete'} />
                                </PoolTextButton>
                            </PoolRow>
                        </Stack>
                    </PoolRow>
                </TableCell>
                <TableCell sx={{ verticalAlign: 'top', width: 274 }}>
                    {cartItem.status !== ShoppingCartItemStatus.Removed &&
                    cartItem.status !== ShoppingCartItemStatus.PriceByRequest ? (
                        <Stack typography="body2" color="grey.800" width={170} alignItems="flex-end">
                            <PoolQuantityPicker
                                quantity={cartItem.quantity}
                                minQuantity={article.minimumOrderQuantity}
                                maxQuantity={article.maximumOrderQuantity}
                                interval={article.quantityInterval}
                                onQuantityChange={handleQuantityChange}
                                sx={{ '& .MuiOutlinedInput-root': { width: 170 } }}
                            />
                            <Box marginTop={0.5}>
                                <FormattedMessage
                                    id="app.cart.article.quantity"
                                    values={{
                                        orderUnit: article.orderUnit,
                                        contentQuantity: article.contentQuantity,
                                        contentUnit: article.contentUnit,
                                        optional: chunks => article.contentQuantity && article.contentUnit && chunks,
                                    }}
                                />
                            </Box>
                            {(article.minimumOrderQuantity ||
                                (article.quantityInterval && article.quantityInterval !== 1)) && (
                                <Stack direction="row">
                                    {'('}
                                    <Stack
                                        direction="row"
                                        typography="body2"
                                        color="grey.800"
                                        gap={1}
                                        divider={<VerticalDivider />}
                                    >
                                        {article.minimumOrderQuantity && (
                                            <Box>
                                                <PoolFormattedMessage
                                                    id={'app.cart.article.minimumQuantity'}
                                                    values={{ minimumQuantity: article.minimumOrderQuantity }}
                                                />
                                            </Box>
                                        )}
                                        {article.quantityInterval && article.quantityInterval !== 1 && (
                                            <PoolFormattedMessage
                                                id={'app.cart.article.interval'}
                                                values={{
                                                    interval: article.quantityInterval,
                                                }}
                                            />
                                        )}
                                    </Stack>
                                    {')'}
                                </Stack>
                            )}
                        </Stack>
                    ) : (
                        <Button variant="outlined" onClick={handleCreateSingleInquiry}>
                            <FormattedMessage id="app.singleInquiry" />
                        </Button>
                    )}
                </TableCell>
                <TableCell align="right" sx={{ verticalAlign: 'top', width: 129 }}>
                    {cartItem.status !== ShoppingCartItemStatus.Removed &&
                        cartItem.status !== ShoppingCartItemStatus.PriceByRequest && (
                            <ArticlePrice article={article} netPrice={cartItem.netPrice} />
                        )}
                </TableCell>

                <TableCell align="right" sx={{ verticalAlign: 'top', width: 120 }}>
                    {cartItem.status !== ShoppingCartItemStatus.Removed &&
                        cartItem.status !== ShoppingCartItemStatus.PriceByRequest && (
                            <Box
                                color={
                                    article.specialPrice?.price ? theme.palette.error.dark : theme.palette.common.black
                                }
                                typography="body1"
                                alignSelf="flex-start"
                                fontWeight={600}
                                width={120}
                                marginLeft="auto"
                            >
                                <FormattedNumber
                                    value={PoolMath.round(cartItem.netPrice * quantity)}
                                    currency={article.currency}
                                    style="currency"
                                />
                            </Box>
                        )}
                </TableCell>
            </TableRow>
        </>
    );
};
